import React, { useState, useCallback } from 'react';
import { Transition } from '@headlessui/react';
import { PlusIcon, ChevronUpIcon, ChevronDownIcon, TrashIcon } from '@heroicons/react/24/outline';

function ArrayField(props) {
  // Handle both direct props and remount attribute props
  const setting = props.setting
  const initialValue = props.initialValue || props['initial-value'] || '[]'
  const placeholder = props.placeholder || 'Add new item'
  const validationType = props.validationType || props['validation-type'] || null
  const onSave = props.onSave
  
  // Parse initialValue if it's a string
  let parsedInitialValue = [];
  try {
    parsedInitialValue = typeof initialValue === 'string' ? JSON.parse(initialValue) : initialValue;
  } catch (e) {
    console.error('Error parsing initialValue:', e);
    parsedInitialValue = [];
  }
  
  const [items, setItems] = useState(parsedInitialValue);
  const [newItem, setNewItem] = useState('');
  const [validationError, setValidationError] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const validateItem = useCallback((value, checkDuplicate = false) => {
    if (validationType === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return 'Invalid email format';
      }
      if (checkDuplicate && items.includes(value)) {
        return 'Email already exists in the list';
      }
    }
    return null;
  }, [items, validationType]);

  const addItem = useCallback(() => {
    const trimmedValue = newItem.trim();
    if (trimmedValue !== '') {
      const error = validateItem(trimmedValue, true);
      if (error) {
        setValidationError(error);
        return;
      }
      setValidationError('');
      setItems(prev => [...prev, trimmedValue]);
      setNewItem('');
    }
  }, [newItem, validateItem]);

  const removeItem = useCallback((index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  }, []);

  const moveUp = useCallback((index) => {
    if (index > 0) {
      setItems(prev => {
        const newItems = [...prev];
        [newItems[index], newItems[index - 1]] = [newItems[index - 1], newItems[index]];
        return newItems;
      });
    }
  }, []);

  const moveDown = useCallback((index) => {
    setItems(prev => {
      if (index < prev.length - 1) {
        const newItems = [...prev];
        [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
        return newItems;
      }
      return prev;
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      // Build the full URL to ensure it works with admin subdomain
      const baseUrl = window.location.origin;
      const response = await fetch(`${baseUrl}/settings/${setting}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({ value: items.join('\n'), field_type: 'array' })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
        if (onSave) onSave(items);
      } else {
        setError(data.error || 'Failed to save setting');
      }
    } catch (e) {
      setError('Network error: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addItem();
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="flex gap-2">
          <input
            type={validationType === 'email' ? 'email' : 'text'}
            value={newItem}
            onChange={(e) => {
              setNewItem(e.target.value);
              setValidationError('');
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={`input input-bordered flex-1 dark:bg-gray-700 dark:text-white ${
              validationError ? 'input-error' : ''
            }`}
          />
          <button
            type="button"
            onClick={addItem}
            className="btn btn-outline btn-primary"
          >
            <PlusIcon className="h-5 w-5" />
            Add
          </button>
        </div>
        <Transition
          show={!!validationError}
          enter="transition-opacity duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <p className="mt-1 text-sm text-error">{validationError}</p>
        </Transition>
      </div>

      <div className="space-y-2">
        {items.map((item, index) => {
          const itemError = validateItem(item);
          return (
            <div
              key={index}
              className={`flex items-center gap-2 p-2 bg-base-200 rounded-md group ${
                itemError ? 'ring-1 ring-error' : ''
              }`}
            >
              <span className={`flex-1 text-sm ${itemError ? 'text-error' : ''}`}>
                {item}
              </span>
              {itemError && (
                <span className="text-xs text-error">{itemError}</span>
              )}

              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  type="button"
                  onClick={() => moveUp(index)}
                  disabled={index === 0}
                  className="btn btn-ghost btn-xs"
                  title="Move up"
                >
                  <ChevronUpIcon className="h-4 w-4" />
                </button>

                <button
                  type="button"
                  onClick={() => moveDown(index)}
                  disabled={index === items.length - 1}
                  className="btn btn-ghost btn-xs"
                  title="Move down"
                >
                  <ChevronDownIcon className="h-4 w-4" />
                </button>

                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  className="btn btn-ghost btn-xs text-error"
                  title="Remove"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
        })}

        {items.length === 0 && (
          <div className="text-center py-4 text-base-content/50 text-sm">
            No items yet. Add one above.
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className="text-xs text-base-content/50">
          {items.length} items
        </div>

        <div className="flex items-center gap-3">
          <Transition
            show={success}
            enter="transition-opacity duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <span className="text-sm text-success">✓ Saved successfully</span>
          </Transition>

          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="btn btn-primary btn-sm"
          >
            {saving ? (
              <>
                <span className="loading loading-spinner loading-xs"></span>
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>

      <Transition
        show={!!error}
        enter="transition-opacity duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-150"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="text-sm text-error">{error}</div>
      </Transition>
    </div>
  );
}

export default ArrayField;